// Util
import { treatErrorAs, handleErrorsInQueue } from 'api/errors';
import {
  determineActiveProductYear
} from 'util/env';

// Constants
import { errorLevel } from 'constants/errors';
import { affiliateIds } from 'constants/affiliates';

// requesters
import { requestActivities } from 'activities/requesters';
import { requestFertilityPracticesData } from 'products/requesters';
import { requestJohnDeereFields, requestJohnDeereWorkPlans } from 'affiliates/requesters';

// finders
import {
  batchFindScriptsForFieldId,
  findAllScriptsForFieldId,
  findPrimaryScriptListForFieldId,
  findScriptById,
  findScriptListForFieldId,
  findScriptsSummariesForFieldId
} from 'scripts/finders';

// selectors
import { isManageScriptListLoadDone } from 'api/loading/selectors';
import {
  getActiveFieldId
} from 'current/selectors';
import {
  getProductYearForScript,
  getScriptsForFieldId,
  getScriptTypeForScript,
  isCompleteScript
} from 'scripts/selectors';
import { hasAffiliateById } from 'affiliates/selectors';
import { getPlantingScriptsIdsForActiveField } from './planting-selectors';
import { areScriptsLoadImprovementsEnabled } from 'user/selectors';

// Actions
import {
  completeActiveProductYearLoading,
  completeScriptListLoading
} from 'api/loading';
import {
  setActiveProductYear,
  setActiveScriptId,
  setActiveScriptType
} from 'current';

// util
import { routeParse } from '../util/routes';

export const requestActiveManageScript = () => {
  return (dispatch, getState) => {
    const state = getState();
    const fieldId = getActiveFieldId(state);

    return Promise.resolve()
    .then( () => Promise.all([
      (areScriptsLoadImprovementsEnabled(state)
        ? findScriptsSummariesForFieldId({ dispatch, getState, fieldId })
        : findScriptListForFieldId({ dispatch, getState, fieldId }))
    ]) )
    .then( () => {
      const state = getState();
      const scripts = getScriptsForFieldId(state, fieldId);
      const isBeaconEnabled = state.launchDarkly?.scriptCreatorBeacon;
      const isConnectedToJD = hasAffiliateById(state, affiliateIds.MYJD);
      const hasPlantingScripts = Boolean(getPlantingScriptsIdsForActiveField(state).length);

      if (isBeaconEnabled && isConnectedToJD && hasPlantingScripts) {
        dispatch(requestJohnDeereFields());
        dispatch(requestJohnDeereWorkPlans());
      }
      // set active product year based on full scripts list -- may be reset when we
      // find an active script
      const activeProductYear = determineActiveProductYear({ getState, scripts });
      dispatch(setActiveProductYear(activeProductYear.toString()));

      const routeHash = (location && location.hash) || '';
      const scriptId = routeParse(routeHash).scriptId;
      if( scriptId && scripts[scriptId] ) {
        return findScriptById({ dispatch, getState, fieldId, scriptId })
        .then( (script) => {
          // as long as the loaded script is complete, go ahead and set to active
          if (isCompleteScript(script)) {
            // set the active product year and script type by the active script
            dispatch( setActiveProductYear(getProductYearForScript(script)) );
            dispatch( setActiveScriptType(getScriptTypeForScript(script)) );
            dispatch( setActiveScriptId(scriptId) ); // It's a winner!  Save it
            dispatch( completeActiveProductYearLoading() );
          }

        });
      } else {
        return null; // we can't load a script first
      }
    })
    .catch( treatErrorAs({ severity: errorLevel.INFO }) );
  };
};

export const requestManageScripts = ({ ignoreRoute = false } = {}) => {
  return (dispatch, getState) => {
    const state = getState();
    const fieldId = getActiveFieldId(state);
    const forceFetch = !isManageScriptListLoadDone(state);

    return Promise.resolve()
    .then( () => dispatch(requestFertilityPracticesData()) )
    // needed to sort fields in manage script list & determine primary for fertility scripts
    .then( () => dispatch(requestActivities()))
    .then( () => Promise.all([
      (areScriptsLoadImprovementsEnabled(getState())
        ? findScriptsSummariesForFieldId({ dispatch, getState, fieldId })
        : findScriptListForFieldId({ dispatch, getState, fieldId, forceFetch }))
    ]) )
    .then( () => {
      dispatch(requestActiveManageScript({ ignoreRoute }));
    })
    .then( () => {
      // Load remaining scripts
      return findAllScriptsForFieldId({dispatch, getState, fieldId})
      .catch( treatErrorAs({ severity: errorLevel.WARN }) )
      .then( (scripts) => {
        dispatch( completeScriptListLoading() );
        return scripts;
      });
    })
    .catch( treatErrorAs({ severity: errorLevel.BLOCK, andStopHere:true }) )
    .then( () => handleErrorsInQueue() );
  };
};

export const requestManualFlowPrimaryScripts = () => {
  return (dispatch, getState) => {
    const state = getState();
    const fieldId = getActiveFieldId(state);

    return findPrimaryScriptListForFieldId({ dispatch, getState, fieldId, forceWait: true })
    .then( (scripts) => {
      return batchFindScriptsForFieldId({ dispatch, getState, fieldId, scripts: Object.values(scripts) });
    } );
  };
};
